import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useAppConfigStore } from '@stores/appConfig.store';
import { useBookingQueryStore } from '@stores/bookingQuery.store';
import { useAgentFlowStore } from '@stores/agentFlow.store';
import {
  resolveAgentEntryRoomRateContext,
  setPendingAgentEntryContext,
} from '@/utils/agentEntryContext';

const PRE_PAYMENT_ROUTE_NAMES = new Set([
  'home',
  'search',
  'room details',
  'rate details',
  'extras',
]);

const CHECKOUT_FOOTER_ROUTE_NAMES = new Set(['extras', 'guest details']);

const FLOATING_ABOVE_FOOTER_ROUTE_NAMES = new Set([
  'room details',
  'rate details',
  ...CHECKOUT_FOOTER_ROUTE_NAMES,
]);

export function useGoToAgent() {
  const route = useRoute();
  const router = useRouter();
  const appConfigStore = useAppConfigStore();
  const bookingQueryStore = useBookingQueryStore();
  const agentFlowStore = useAgentFlowStore();
  const { applicationSetup, setupLoaded } = storeToRefs(appConfigStore);
  const { hideAgentButton } = storeToRefs(agentFlowStore);
  const { buildUrlQueryFromBookingQuery } = bookingQueryStore;

  const isAgentEnabled = computed(() => {
    const agent = applicationSetup.value?.booking_engine_agent;
    return Boolean(agent) && agent.active !== false;
  });

  const isPrePaymentRoute = computed(() =>
    PRE_PAYMENT_ROUTE_NAMES.has(route.name),
  );

  const canShowAgenticButton = computed(
    () =>
      setupLoaded.value &&
      isAgentEnabled.value &&
      hideAgentButton.value === false &&
      isPrePaymentRoute.value,
  );

  const hasCheckoutFooter = computed(() =>
    CHECKOUT_FOOTER_ROUTE_NAMES.has(route.name),
  );

  const agenticButtonAboveFooter = computed(() =>
    FLOATING_ABOVE_FOOTER_ROUTE_NAMES.has(route.name),
  );

  const goToAgent = async () => {
    const currentRoute = router.currentRoute.value;
    const urlHasDates = Boolean(currentRoute.query.startDay);

    agentFlowStore.clearEntryRoomRateContext();
    setPendingAgentEntryContext(null);

    let reservationStore = null;
    if (currentRoute.name === 'guest details') {
      const { useReservationStore } = await import(
        '@/stores/reservation.store'
      );
      reservationStore = useReservationStore();
    }

    const entryContext = resolveAgentEntryRoomRateContext(
      currentRoute,
      reservationStore,
    );
    const hasEntryRoomRate = Boolean(entryContext?.roomRateId);

    agentFlowStore.syncFromBookingQuery();

    const apiKeyParamName = import.meta.env.VITE_APIKEY_PARAM_NAME;
    const bookingParams = buildUrlQueryFromBookingQuery();
    const storeHasDates = Boolean(bookingParams.startDay);

    // Normally the agent only inherits dates that are present in the current
    // URL. But when the user launches from a page tied to a specific room/rate
    // plan, keep the dates from the store so the agent can price that exact
    // selection instead of asking for dates again.
    const includeDates = urlHasDates || (hasEntryRoomRate && storeHasDates);

    if (!includeDates) {
      agentFlowStore.setCheckInDate(null);
      agentFlowStore.setCheckOutDate(null);
    }

    const agentQuery = bookingQueryStore.stripOccupancyFromUrlQuery({
      ...bookingParams,
    });

    if (!includeDates) {
      delete agentQuery.startDay;
      delete agentQuery.nrNights;
    }

    if (apiKeyParamName && currentRoute.query[apiKeyParamName]) {
      agentQuery[apiKeyParamName] = currentRoute.query[apiKeyParamName];
    }
    if (currentRoute.query.channelKey) {
      agentQuery.channelKey = currentRoute.query.channelKey;
    }
    if (typeof window !== 'undefined' && window.self !== window.top) {
      agentQuery.iframeMode = '1';
    }

    if (hasEntryRoomRate) {
      agentFlowStore.setEntryRoomRateContext(
        entryContext.roomRateId,
        entryContext.detailsView,
      );
      setPendingAgentEntryContext(entryContext);
    }

    router.push({
      name: 'agent',
      query: agentQuery,
      state: entryContext
        ? {
            entryRoomRateId: entryContext.roomRateId,
            entryDetailsView: entryContext.detailsView,
          }
        : {},
    });
  };

  return {
    canShowAgenticButton,
    goToAgent,
    hasCheckoutFooter,
    agenticButtonAboveFooter,
    isPrePaymentRoute,
  };
}
